import { Button, Group, Select, Stack, Text, Textarea } from "@mantine/core";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { friendlyZoneName, timeInZone, timeZoneOptions } from "../format/time-zones";
import { ConfirmActionModal } from "./ConfirmActionModal";

export interface TimeZoneFieldProps {
  /** The zone in force now (an IANA name). */
  readonly value: string;
  /** Whether the viewer may change it. The server decides again on save. */
  readonly canChange: boolean;
  /** Shown under the field when the viewer may not change it. */
  readonly lockedMessage: ReactNode;
  /** Ask for a note saying why (DCX support's changes carry one into the church's audit log). */
  readonly requireReason?: boolean;
  /** Saves the zone. Reject with an Error whose message the person can act on; it is shown in the dialog. */
  readonly onSave: (zone: string, reason: string) => Promise<void>;
  /** The church's name, so the confirmation names what changes. */
  readonly churchName?: string;
  readonly label?: string;
}

/** The sentence every confirmation carries (owner, 2026-10-03). */
export const TIME_ZONE_EFFECT =
  "Event times, schedules and the website's Live now block will follow the new time zone.";

/** Ticks once a minute, so a "now" time shown on a page stays true while the page is open. */
export function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  return now;
}

/**
 * The church's time zone: a searchable list of IANA zones, each with its time now, and a confirmation that says what
 * follows the change. Read-only, with the reason, for anyone who may not change it.
 */
export function TimeZoneField({
  value,
  canChange,
  lockedMessage,
  requireReason = false,
  onSave,
  churchName,
  label = "Time zone",
}: TimeZoneFieldProps) {
  const now = useMinuteClock();
  const options = useMemo(() => timeZoneOptions(now, [value]), [now, value]);
  const [chosen, setChosen] = useState(value);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setChosen(value);
  }, [value]);

  const changed = chosen !== value;
  const reasonMissing = requireReason && reason.trim().length === 0;
  const nowThere = timeInZone(value, now);

  const close = (): void => {
    if (saving) return;
    setConfirming(false);
    setError(null);
  };

  const confirm = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onSave(chosen, reason.trim());
      setConfirming(false);
      setReason("");
      // The Change button disables itself once the zone is saved; focus goes back to the field on purpose.
      selectRef.current?.focus();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The time zone was not changed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="xs">
      <Select
        ref={selectRef}
        label={label}
        description={
          nowThere === null
            ? friendlyZoneName(value)
            : `${friendlyZoneName(value)} — ${nowThere} there now`
        }
        data={options}
        value={chosen}
        onChange={(next) => {
          if (next !== null) setChosen(next);
        }}
        searchable
        nothingFoundMessage="No time zone by that name"
        allowDeselect={false}
        maxDropdownHeight={280}
        disabled={!canChange}
        comboboxProps={{ withinPortal: true }}
      />
      {canChange ? (
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Text size="xs" c="dimmed">
            {TIME_ZONE_EFFECT}
          </Text>
          <Button
            variant="default"
            disabled={!changed}
            style={{ flexShrink: 0 }}
            onClick={() => {
              setConfirming(true);
            }}
          >
            Change time zone
          </Button>
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          {lockedMessage}
        </Text>
      )}
      <ConfirmActionModal
        opened={confirming}
        onClose={close}
        onConfirm={confirm}
        title={`Change ${churchName === undefined ? "the" : `${churchName}'s`} time zone to ${friendlyZoneName(chosen)}?`}
        message={`${TIME_ZONE_EFFECT} Events already on the calendar stay at the same moment, so their clock times move by the difference. A campus with its own time zone keeps it.`}
        confirmLabel="Change time zone"
        loading={saving}
        confirmDisabled={reasonMissing}
      >
        {requireReason ? (
          <Textarea
            label="Why is it changing?"
            description="The church sees this note in its audit log."
            value={reason}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setReason(next);
            }}
            maxLength={300}
            autosize
            minRows={2}
            required
          />
        ) : null}
        {error === null ? null : (
          <Text size="sm" c="red" role="alert">
            {error}
          </Text>
        )}
      </ConfirmActionModal>
    </Stack>
  );
}
